import React from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { XBoldIcon } from '@pattern-library/icon';
import throttle from 'lodash/throttle';
import OutsideClickHandler from '@pattern-library/outside-click-handler';
import FocusTrap from '@pattern-library/focus-trap';
import { getTransformedParentElement } from './utils';
const isDocumentObject = typeof document === 'object';
const propTypes = {
  /**
   * Position of arrow and message popover relative to target element
   * (`horizontal: center` position ignores `horizontalOffset` value)
   **/
  arrowPosition: PropTypes.shape({
    vertical: PropTypes.oneOf(['top', 'bottom']),
    horizontal: PropTypes.oneOf(['left', 'center', 'right'])
  }),
  /**
   * HTML content that appears in the body of the popover.
   **/
  children: PropTypes.node,
  /**
   * Custom classes to apply to the message popover container.
   */
  className: PropTypes.string,
  /**
   * Title for close button to help screen readers
   **/
  closeButtonTitle: PropTypes.string,
  /**
   * If enabled, message popover closes when user clicks outside of the container
   **/
  closeOnOutsideClick: PropTypes.bool,
  /**
   * Send focus to the message popover when it opens
   */
  focusOnOpen: PropTypes.bool,
  /**
   * Send focus to the target element when the message popover closes.
   * Requires target prop to be set.
   */
  focusTargetOnClose: PropTypes.bool,
  /**
   * Horizontal distance from target element (not available for center horizontal positioning)
   **/
  horizontalOffset: PropTypes.number,
  /**
   * ID for the message popover content div
   */
  id: PropTypes.string,
  /**
   * Message popover visibility state managed by parent component
   **/
  isOpen: PropTypes.bool,
  /**
   * max-width style applied to message popover containing div.
   * <br />Must be a valid CSS unit value.
   */
  maxWidth: PropTypes.string,
  /**
   * Callback function for message popover close event.
   * This event should toggle the `isOpen` prop current value.
   **/
  onClose: PropTypes.func,
  /**
   * Display style for the outside click handler container div.
   */
  outsideClickDisplay: PropTypes.oneOf(['block', 'flex', 'inline', 'inline-block', 'contents']),
  /**
   * Identifier for automation tests applied to the container div.
   **/
  qaId: PropTypes.string,
  /**
   * The target element that the message popover will point to.
   * <br />Accepts the ID string of the DOM element, or a ref object.
   **/
  target: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  /**
   * Automatically close the popover when the user scrolls the window
   * and the target element is no longer visible
   */
  useAutoCloseOnScroll: PropTypes.bool,
  /**
   * Flag to check if the close button needs to be displayed
   **/
  useCloseButton: PropTypes.bool,
  /**
   * Use fixed positioning to place the popover (recommended for most cases)
   */
  useFixedPositioning: PropTypes.bool,
  /**
   * When set to true, the popover will be rendered at the end of the body tag
   * instead of where the component is instantiated on the page.
   */
  usePortal: PropTypes.bool,
  /**
   * Automatically reposition the popover if it overflows the window
   */
  useResponsivePositioning: PropTypes.bool,
  /**
   * Vertical distance from target element
   **/
  verticalOffset: PropTypes.number
};
const defaultProps = {
  arrowPosition: {
    vertical: 'bottom',
    horizontal: 'left'
  },
  children: [],
  closeButtonTitle: 'Close message',
  closeOnOutsideClick: false,
  focusOnOpen: false,
  focusTargetOnClose: false,
  horizontalOffset: 40,
  isOpen: true,
  onClose: () => {},
  outsideClickDisplay: 'inline',
  qaId: '',
  target: '',
  useAutoCloseOnScroll: false,
  useCloseButton: false,
  useFixedPositioning: true,
  usePortal: false,
  useResponsivePositioning: false,
  verticalOffset: 16
};
class MessagePopover extends React.Component {
  constructor(props) {
    super(props);
    this.initState = () => {
      return {
        top: 0,
        left: 0,
        popoverHeight: 0,
        popoverWidth: 0,
        targetWidth: 0,
        arrowPosition: {
          ...this.props.arrowPosition
        }
      };
    };
    this.handleKeyDown = event => {
      if (event.key === 'Escape') {
        this.props.onClose();
      }
    };
    this.addEventListeners = () => {
      const {
        useFixedPositioning
      } = this.props;
      if (useFixedPositioning) {
        window.addEventListener('resize', this.handleWindowChanges);
        window.addEventListener('scroll', this.handleWindowChanges, true);
      }
      this.popoverRef.addEventListener('keydown', this.handleKeyDown);
    };
    this.removeEventListeners = () => {
      const {
        useFixedPositioning
      } = this.props;
      if (useFixedPositioning) {
        this.handleWindowChanges.cancel();
        window.removeEventListener('resize', this.handleWindowChanges);
        window.removeEventListener('scroll', this.handleWindowChanges);
      }
      this.popoverRef.removeEventListener('keydown', this.handleKeyDown);
    };
    this.positionPopup = () => {
      const targetOffset = this.getTargetOffset();
      if (!this.popoverRef || !targetOffset) return;
      const {
        arrowPosition
      } = this.state;
      const {
        width,
        height
      } = this.popoverRef.getBoundingClientRect();
      const top = arrowPosition.vertical === 'bottom' ? this.positionPopopAbove(targetOffset, height) : this.positionPopopBelow(targetOffset);
      let left = arrowPosition.horizontal === 'left' ? this.setLeftAligned(targetOffset) : this.props.arrowPosition.horizontal === 'center' ? this.setCenterAligned(targetOffset, width) : this.setRightAligned(targetOffset, width);
      this.setState({
        top,
        left
      }, () => this.checkVisibility(true));
    };
    this.checkVisibility = isFirstPass => {
      if (this.props.useResponsivePositioning) {
        const targetOffset = this.getTargetOffset();
        const {
          arrowPosition: currentArrowPosition
        } = this.state;
        const {
          top,
          left,
          right,
          bottom,
          width,
          height
        } = this.popoverRef.getBoundingClientRect();
        const windowHeight = window.outerHeight;
        const windowWidth = window.outerWidth;
        let newTop = this.state.top;
        if (top < 0 && height <= windowHeight && currentArrowPosition.vertical === 'bottom') {
          newTop = this.positionPopopBelow(targetOffset);
          currentArrowPosition.vertical = 'top';
        } else if (bottom > windowHeight && currentArrowPosition.vertical === 'top') {
          newTop = this.positionPopopAbove(targetOffset, height);
          currentArrowPosition.vertical = 'bottom';
        }
        let newLeft = this.state.left;
        if (width > windowWidth) {
          newLeft = this.setCenterAligned(targetOffset, width);
          currentArrowPosition.horizontal = 'center';
        } else if (left < 0 && currentArrowPosition.horizontal === 'right') {
          newLeft = this.setLeftAligned(targetOffset);
          currentArrowPosition.horizontal = 'left';
        } else if (right > windowWidth && currentArrowPosition.horizontal === 'left') {
          newLeft = this.setRightAligned(targetOffset, width);
          currentArrowPosition.horizontal = 'right';
        }
        this.setState({
          top: newTop,
          left: newLeft,
          arrowPosition: currentArrowPosition
        }, () => isFirstPass && this.checkVisibility(false));
      }
    };
    this.resetState = () => {
      this.setState({
        arrowPosition: {
          ...this.props.arrowPosition
        }
      });
    };
    this.findClosestOverflowParentOrWindow = element => {
      if (!element) return document;
      let currentElement = element.parentElement;
      while (currentElement) {
        const style = window.getComputedStyle(currentElement);
        const overflow = style.overflow + style.overflowY;
        if (overflow.includes('auto') || overflow.includes('scroll')) {
          return currentElement;
        }
        currentElement = currentElement.parentElement;
      }
      return document;
    };
    this.setObserver = () => {
      const {
        target,
        useAutoCloseOnScroll
      } = this.props;
      const intersectionObserverIsSupported = typeof IntersectionObserver !== 'undefined';
      if (useAutoCloseOnScroll && intersectionObserverIsSupported && target && this.popoverRef) {
        this.rootElement = this.findClosestOverflowParentOrWindow(this.popoverRef);
        this.observer = new IntersectionObserver(_ref => {
          let [entry] = _ref;
          if (!entry.isIntersecting) {
            this.props.onClose();
          }
        }, {
          root: this.rootElement,
          threshold: 0.5
        });
        if (typeof target === 'string') {
          this.targetElement = document.getElementById(target);
        } else if (target && target.current) {
          this.targetElement = target.current;
        }
        if (this.targetElement) {
          this.observer.observe(this.targetElement);
        }
      }
    };
    this.openPopover = () => {
      this.resetState();
      this.updatePopoverDimensions();
      this.addEventListeners();
      this.setObserver();
      if (this.props.focusOnOpen) {
        this.popoverRef.focus();
      }
    };
    this.closePopover = () => {
      this.removeEventListeners();
      if (this.observer) {
        this.observer.disconnect();
      }
      if (this.props.focusTargetOnClose && this.props.target) {
        this.getTargetElement().focus({
          preventScroll: true
        });
      }
    };
    this.handleClickOutside = event => {
      const {
        isOpen,
        onClose
      } = this.props;
      const isElementInsidePopover = this.popoverRef.contains(event.target);
      const targetElement = this.getTargetElement();
      const isElementInsideCloseButton = targetElement.contains(event.target);

      // If external close button is clicked, toggle it to prevent state mishaps
      // If the popover or an element within it is clicked, prevent closing
      if (isElementInsideCloseButton || isElementInsidePopover) return;
      if (isOpen && this.popoverRef) {
        onClose();
      }
    };
    this.getArrowStyles = arrowHorizontalPositon => {
      const {
        horizontalOffset
      } = this.props;
      const {
        targetWidth
      } = this.state;
      const arrowWidth = 26; // .asc-message-popover--arrow: border-width * 2
      const arrowOffsetMargin = horizontalOffset + targetWidth / 2 - arrowWidth / 2 - 1;
      let arrowStyles = {};
      switch (arrowHorizontalPositon) {
        case 'left':
          arrowStyles = {
            marginLeft: arrowOffsetMargin
          };
          break;
        case 'center':
          arrowStyles = {
            marginLeft: arrowWidth / -2
          };
          break;
        case 'right':
          arrowStyles = {
            marginRight: arrowOffsetMargin
          };
          break;
        default:
          break;
      }
      return arrowStyles;
    };
    this.state = this.initState();
    this.handleWindowChanges = throttle(this.positionPopup.bind(this), 10);
  }
  componentDidMount() {
    const {
      isOpen
    } = this.props;
    this.updatePopoverDimensions();
    if (isOpen) {
      this.openPopover();
    }
  }
  componentWillUnmount() {
    this.removeEventListeners();
  }
  componentDidUpdate(prevProps) {
    const {
      horizontalOffset,
      isOpen,
      useFixedPositioning,
      verticalOffset
    } = this.props;
    if (prevProps.horizontalOffset !== horizontalOffset || prevProps.verticalOffset !== verticalOffset || prevProps.useFixedPositioning !== useFixedPositioning) {
      this.updatePopoverDimensions();
    }
    if (isOpen && !prevProps.isOpen) {
      this.openPopover();
    } else if (!isOpen && prevProps.isOpen) {
      this.closePopover();
    }
  }
  updatePopoverDimensions() {
    const target = this.getTargetElement();
    if (target === null) return;
    const {
      width: popoverWidth,
      height: popoverHeight
    } = this.popoverRef.getBoundingClientRect();
    const {
      width: targetWidth
    } = target.getBoundingClientRect();
    this.setState({
      popoverWidth: popoverWidth,
      popoverHeight: popoverHeight,
      targetWidth
    }, this.positionPopup);
  }
  getTargetElement() {
    const {
      target
    } = this.props;
    if (typeof target === 'string') {
      return document.getElementById(target);
    }
    return target?.current || null;
  }
  positionPopopAbove(_ref2, popoverHeight) {
    let {
      top
    } = _ref2;
    const {
      verticalOffset
    } = this.props;
    return Math.floor(top - (popoverHeight + verticalOffset));
  }
  positionPopopBelow(_ref3) {
    let {
      bottom
    } = _ref3;
    const {
      verticalOffset
    } = this.props;
    return Math.ceil(bottom + verticalOffset);
  }
  setLeftAligned(_ref4) {
    let {
      left
    } = _ref4;
    const {
      horizontalOffset
    } = this.props;
    return Math.floor(left - horizontalOffset);
  }
  setCenterAligned(_ref5, popoverWidth) {
    let {
      left
    } = _ref5;
    const {
      targetWidth
    } = this.state;
    return Math.floor(left + targetWidth / 2 - popoverWidth / 2);
  }
  setRightAligned(_ref6, popoverWidth) {
    let {
      right
    } = _ref6;
    const {
      horizontalOffset
    } = this.props;
    return Math.ceil(right - popoverWidth + horizontalOffset);
  }
  getTargetOffset() {
    const {
      useFixedPositioning
    } = this.props;
    const target = this.getTargetElement();
    if (!target) return null;
    const {
      top,
      left,
      right,
      bottom,
      width,
      height
    } = target.getBoundingClientRect();
    const transformedParentElement = getTransformedParentElement(target);
    if (transformedParentElement && useFixedPositioning) {
      // when the target element has a parent with a CSS transform property,
      // the target element fixed position coordinated must be relative to
      // that parent instead of the window
      const parentRect = transformedParentElement.getBoundingClientRect();
      return {
        top: top - parentRect.top,
        left: left - parentRect.left,
        right: right - parentRect.left,
        bottom: bottom - parentRect.top,
        width: width,
        height: height
      };
    }
    if (useFixedPositioning) {
      return {
        top,
        left,
        right,
        bottom
      };
    }
    return {
      top: 0,
      left: 0,
      right: width,
      bottom: height
    };
  }
  render() {
    const {
      className,
      qaId,
      id,
      onClose,
      isOpen,
      maxWidth,
      outsideClickDisplay,
      useCloseButton,
      useFixedPositioning,
      usePortal,
      closeButtonTitle,
      children,
      closeOnOutsideClick,
      focusOnOpen
    } = this.props;
    const {
      arrowPosition,
      top,
      left
    } = this.state;
    const popoverInlineStyles = {
      maxWidth,
      top,
      left
    };
    const classString = classNames(className, 'asc-message-popover', `arrow-${arrowPosition.vertical}-${arrowPosition.horizontal}`, {
      'is-hidden': !isOpen,
      'asc-message-popover--fixed': useFixedPositioning
    });
    const arrowStyles = this.getArrowStyles(arrowPosition.horizontal);
    const popoverContent = /*#__PURE__*/React.createElement(React.Fragment, null, useCloseButton && /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "asc-message-popover--xicon",
      onClick: onClose,
      title: closeButtonTitle
    }, /*#__PURE__*/React.createElement(XBoldIcon, {
      size: "xxs"
    })), /*#__PURE__*/React.createElement("div", {
      id: id
    }, children));
    const canUsePortal = usePortal && isDocumentObject;
    const renderPopover = () => /*#__PURE__*/React.createElement(OutsideClickHandler, {
      disabled: !closeOnOutsideClick,
      display: outsideClickDisplay,
      onOutsideClick: this.handleClickOutside
    }, /*#__PURE__*/React.createElement("div", {
      "data-qa": qaId,
      className: classString,
      style: popoverInlineStyles,
      tabIndex: "-1",
      role: "dialog",
      ref: node => {
        this.popoverRef = node;
      }
    }, /*#__PURE__*/React.createElement("span", {
      className: "asc-message-popover--arrow",
      style: arrowStyles
    }), focusOnOpen ? /*#__PURE__*/React.createElement(FocusTrap, null, popoverContent) : popoverContent));
    return canUsePortal ? /*#__PURE__*/createPortal(renderPopover(), document.body) : renderPopover();
  }
}
MessagePopover.propTypes = propTypes;
MessagePopover.defaultProps = defaultProps;
export default MessagePopover;